// src/components/Layout.js
//
// Sacred Canvas shell — RTL-anchored right sidebar (288px),
// glassmorphic top bar, mobile bottom-tab navigation.

import React, { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import BayanIcon, { toneForSurface } from './BayanIcon';
import {
  Alert, AppBar, Avatar, Box, Chip, Divider, Drawer, IconButton,
  InputBase, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem,
  Paper, Toolbar, Tooltip, Typography, useTheme, useMediaQuery, Button, Popper, ClickAwayListener,
  Slide, useScrollTrigger,
} from '@mui/material';
import { Brightness4, Brightness7, CloudDone, CloudOff, TextFields, Quiz as QuizIcon } from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useThemeContext } from '../contexts/ThemeContext';
import { useFont } from '../contexts/FontContext';
import { REPORT_FONT_OPTIONS, getStoredReportFontKey, setStoredReportFontKey } from '../services/reportFonts';
import { isParentRole, isStudentRole, ROLES } from '../services/userService';
import { studentService, classService } from '../services/firebaseService';
import { repairDemoAdminProfile } from '../services/demoAdminRepair';
import useOnlineStatus from '../hooks/useOnlineStatus';
import NotificationCenter from './NotificationCenter';
import SttHealthIndicator from './SttHealthIndicator';
import MobileTopBar from './mobile/MobileTopBar';
import MobileBottomNav, { MOBILE_BOTTOM_NAV_HEIGHT } from './mobile/MobileBottomNav';
import { buildSearchHaystack, nameMatchScore } from '../utils/arabicNameSearch';


// The desktop sidebar is an icon-only rail; hovering an item flies out its
// label and a shared highlight slides between items (Siri-style).
const RAIL_WIDTH = 76;
const NAV_ITEM_H = 52;   // preferred (and maximum) per-item slot height
const NAV_ITEM_H_MIN = 34; // floor before the rail is allowed to scroll
const NAV_GAP = 6;

const Layout = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colorMode = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [fontAnchor, setFontAnchor] = useState(null);
  const font = useFont();
  // Report font (PDF reports) is independent of the on-screen font, since a
  // PDF must embed a real TTF. Persisted in localStorage via reportFonts.
  const [reportFontKey, setReportFontKey] = useState(getStoredReportFontKey);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchAnchor, setSearchAnchor] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchIndex, setSearchIndex] = useState({ students: [], classes: [], loaded: false });
  const navigate = useNavigate();
  const location = useLocation();
  const drawerWidth = RAIL_WIDTH;
  // Which nav item the sliding highlight currently rests on (hover wins,
  // else the active route).
  const [navHoverIdx, setNavHoverIdx] = useState(null);
  // Vertical scroll offset of the icon list. The rail list scrolls when the
  // window is too short to fit every icon (windowed mode on a 1080p screen,
  // admin role with 10 items). We track it so the morphing highlight pill —
  // which lives in a non-scrolling layer so it can grow leftward out of the
  // rail — stays aligned with its icon as the list scrolls.
  const [railScroll, setRailScroll] = useState(0);
  // Per-item slot height, computed so every icon fits the rail without
  // scrolling on any desktop height. Falls back to scrolling only once the
  // floor (NAV_ITEM_H_MIN) can't fit. railRef measures the available column.
  const [itemH, setItemH] = useState(NAV_ITEM_H);
  const railRef = useRef(null);
  // Root of the desktop rail — used to trap the mouse wheel so scrolling while
  // the pointer is over the bar never bleeds through to the page.
  const sidebarRef = useRef(null);
  const { currentUser, logout, role, userProfile, tenantId, authReady, profileLoading, refreshProfile } = useAuth();
  const [autoRepair, setAutoRepair] = useState({ running: false, attempted: false, error: '' });

  const displayName =
    userProfile?.displayName ||
    currentUser?.displayName ||
    (currentUser?.email ? currentUser.email.split('@')[0] : '') ||
    t('auth.defaultUserLabel');
  const userInitial = (displayName || '؟').trim().charAt(0);
  const online = useOnlineStatus();

  // Hide the top bar on scroll-down, reveal on scroll-up (standard app-bar
  // pattern). Hysteresis on so it only reacts to a deliberate scroll; the
  // threshold lets the first screenful stay put before the bar tucks away
  // as the user reaches the students table below the fold.
  const hideAppBar = useScrollTrigger({ threshold: 64 });

  // Lazy-load a tiny in-memory search index the first time the user focuses the bar
  const ensureSearchIndex = async () => {
    if (searchIndex.loaded || isParentRole(role) || isStudentRole(role)) return;
    try {
      const [students, classes] = await Promise.all([
        studentService.getStudents().catch(() => []),
        classService.getClasses().catch(() => []),
      ]);
      setSearchIndex({ students, classes, loaded: true });
    } catch (_) { /* noop */ }
  };

  // Auto-repair the demo admin's tenant + user doc on first render
  // when we detect the broken state. One-shot per session: if it fails,
  // the alert below surfaces a manual retry button.
  useEffect(() => {
    if (!authReady || profileLoading) return;
    if (!currentUser || tenantId || autoRepair.attempted) return;
    if (currentUser.email !== 'jamal.mazraoui@director.albayan.com') return;
    setAutoRepair({ running: true, attempted: true, error: '' });
    (async () => {
      try {
        await repairDemoAdminProfile(currentUser);
        if (typeof refreshProfile === 'function') await refreshProfile();
        setAutoRepair({ running: false, attempted: true, error: '' });
      } catch (err) {
        console.error('[demo-repair auto]', err);
        setAutoRepair({
          running: false,
          attempted: true,
          error: err?.code || err?.message || 'خطأ غير معروف',
        });
      }
    })();
  }, [authReady, profileLoading, currentUser, tenantId, autoRepair.attempted, refreshProfile]);

  // Fuzzy Arabic↔French haystacks — same engine the director table uses, so
  // "Yasi" / "ياسين" / "yassine" all match the same student. Built once per
  // index load and reused on every keystroke (classes reuse the student
  // builder by aliasing their name into studentFullName).
  const searchHaystacks = useMemo(() => {
    const students = new Map();
    for (const s of searchIndex.students) students.set(s.id, buildSearchHaystack(s));
    const classes = new Map();
    for (const c of searchIndex.classes) classes.set(c.id, buildSearchHaystack({ studentFullName: c.name }));
    return { students, classes };
  }, [searchIndex]);

  useEffect(() => {
    if (!searchQuery || !searchIndex.loaded) { setSearchResults([]); return; }
    const q = searchQuery.trim();
    if (!q) { setSearchResults([]); return; }
    // Score, drop non-matches, rank best-first, then cap.
    const rank = (list, getId) => list
      .map((it) => ({ it, score: nameMatchScore(getId(it), q) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score);
    const matches = [
      ...rank(searchIndex.students, (s) => searchHaystacks.students.get(s.id))
        .slice(0, 6)
        .map(({ it: s }) => ({ kind: 'student', id: s.id, label: s.studentFullName, sub: s.fatherFullName || '', icon: <BayanIcon name="profile_avatar" fontSize="small" /> })),
      ...rank(searchIndex.classes, (c) => searchHaystacks.classes.get(c.id))
        .slice(0, 4)
        .map(({ it: c }) => ({ kind: 'class', id: c.id, label: c.name, sub: c.level || '', icon: <BayanIcon name="student" fontSize="small" /> })),
    ];
    setSearchResults(matches);
  }, [searchQuery, searchIndex, searchHaystacks]);

  const { sidebarItems, bottomItems } = useMemo(() => {
    if (isStudentRole(role)) {
      const items = [
        { text: t('navigation.studentHome'), icon: <BayanIcon name="home_dashboard" />, path: '/student/home' },
        { text: t('navigation.studentReview'), icon: <BayanIcon name="repeat_loop" />, path: '/student/review' },
        { text: t('navigation.studentQuiz'), icon: <QuizIcon />, path: '/student/quiz' },
        { text: t('navigation.studentTadabbur'), icon: <BayanIcon name="open_mushaf" />, path: '/student/tadabbur' },
      ];
      // almada §5.1 — student bottom tabs: الورد · الاختبار · السماع · التدبر · الملف
      const bottom = [
        { text: 'الورد', icon: <BayanIcon name="home_dashboard" />, path: '/student/home' },
        { text: 'الاختبار', icon: <QuizIcon />, path: '/student/quiz' },
        { text: 'السماع', icon: <BayanIcon name="lips_pronounce" />, path: '/student/recite' },
        { text: 'التدبر', icon: <BayanIcon name="open_mushaf" />, path: '/student/tadabbur' },
        { text: 'الملف', icon: <BayanIcon name="director_moudir" />, path: '/profile' },
      ];
      return { sidebarItems: items, bottomItems: bottom };
    }
    if (isParentRole(role)) {
      const items = [
        { text: t('navigation.parentDashboard'), icon: <BayanIcon name="family_parent" />, path: '/parent/dashboard' },
        { text: t('navigation.reports'), icon: <BayanIcon name="bar_chart" />, path: '/parent/reports' },
      ];
      // almada §5.1 — parent bottom tabs: الطفل · التقارير · الشهادات · الدردشة · الملف
      const bottom = [
        { text: 'الطفل', icon: <BayanIcon name="family_parent" />, path: '/parent/dashboard' },
        { text: 'التقارير', icon: <BayanIcon name="bar_chart" />, path: '/parent/reports' },
        { text: 'الشهادات', icon: <BayanIcon name="trophy" />, path: '/parent/certificates' },
        { text: 'الملف', icon: <BayanIcon name="director_moudir" />, path: '/profile' },
      ];
      return { sidebarItems: items, bottomItems: bottom };
    }
    const full = [
      { text: t('navigation.dashboard'), icon: <BayanIcon name="home_dashboard" />, path: '/dashboard' },
      { text: t('navigation.attendance'), icon: <BayanIcon name="calendar_present" />, path: '/attendance' },
      { text: t('navigation.dailyProgress'), icon: <BayanIcon name="edit_pencil" />, path: '/daily-progress' },
      // FSRS / muraja'a module is dormant — entry points hidden until the
      // bridge from wirdRatings → reviewQueue is re-enabled. Routes still
      // exist (`/revision-queue`, `/memorization-health`) for direct
      // testing, but no nav surface points at them.
      { text: t('navigation.students'), icon: <BayanIcon name="halaqa_circle" />, path: '/students' },
      { text: t('navigation.classes'), icon: <BayanIcon name="student" />, path: '/classes' },
      { text: t('navigation.curriculum'), icon: <BayanIcon name="month_calendar" />, path: '/curriculum' },
      { text: t('navigation.certificates'), icon: <BayanIcon name="trophy" />, path: '/certificates' },
      { text: t('navigation.reports'), icon: <BayanIcon name="bar_chart" />, path: '/reports' },
    ];
    if (role === ROLES.ADMIN) {
      full.push({ text: t('navigation.admin'), icon: <BayanIcon name="director_moudir" />, path: '/admin' });
      full.push({ text: t('navigation.sttLab'), icon: <BayanIcon name="open_mushaf" />, path: '/admin/stt-lab' });
    }
    // almada §5.1 — admin: اللوحة · الحلقات · الطلاب · الاقتراحات · الإعدادات
    //               teacher (mobile Revolut-style, 5 slots with center FAB):
    //                 اللوحة · الحضور · [FAB تسميع] · الطلاب · الملف
    const isAdmin = role === ROLES.ADMIN;
    const bottom = isAdmin
      ? [
          { text: 'اللوحة', icon: <BayanIcon name="home_dashboard" />, path: '/dashboard' },
          { text: 'الحلقات', icon: <BayanIcon name="student" />, path: '/classes' },
          { text: 'الطلاب', icon: <BayanIcon name="halaqa_circle" />, path: '/students' },
          { text: 'الاقتراحات', icon: <BayanIcon name="trend_chart" />, path: '/students/suggestions' },
          { text: 'الإعدادات', icon: <BayanIcon name="director_moudir" />, path: '/admin' },
        ]
      : [
          { text: 'اللوحة', icon: <BayanIcon name="home_dashboard" />, path: '/dashboard' },
          { text: 'الحضور', icon: <BayanIcon name="calendar_present" />, path: '/attendance' },
          { text: 'تسميع', icon: <BayanIcon name="open_mushaf" tone="yellow" />, path: '/daily-progress', isFab: true },
          { text: 'الطلاب', icon: <BayanIcon name="halaqa_circle" />, path: '/students' },
          { text: 'الملف', icon: <BayanIcon name="director_moudir" />, path: '/profile' },
        ];
    return { sidebarItems: full, bottomItems: bottom };
  }, [t, role]);

  const handleMenuClick = (path) => {
    navigate(path);
  };
  const handleProfileMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);
  const handleLogout = async () => {
    try { await logout(); navigate('/login'); }
    catch (error) { console.error(error); }
    handleProfileMenuClose();
  };

  const selectedNavIdx = sidebarItems.findIndex((item) => location.pathname.startsWith(item.path));
  const highlightIdx = navHoverIdx != null ? navHoverIdx : selectedNavIdx;

  const hovering = navHoverIdx != null;

  // Fit every icon into the rail's available height. The rail container is
  // flex-driven (independent of item height thanks to minHeight:0 + overflow),
  // so measuring it and dividing by the item count has no layout feedback loop.
  const navCount = sidebarItems.length;
  useLayoutEffect(() => {
    const el = railRef.current;
    if (!el) return undefined;
    const recompute = () => {
      const avail = el.clientHeight;
      if (!avail || !navCount) return;
      const raw = (avail - NAV_GAP * (navCount - 1)) / navCount;
      const next = Math.max(NAV_ITEM_H_MIN, Math.min(NAV_ITEM_H, Math.floor(raw)));
      setItemH((prev) => (prev !== next ? next : prev));
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [navCount]);

  // Trap the mouse wheel over the rail: stop it from scrolling the page, but
  // still drive the inner icon list when it actually overflows. A native
  // listener with passive:false is required — React's synthetic onWheel is
  // passive, so preventDefault() there is a no-op.
  useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      const list = railRef.current;
      if (list && list.scrollHeight > list.clientHeight) {
        list.scrollTop += e.deltaY;
      }
      e.preventDefault();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const sidebar = (
    <Box ref={sidebarRef} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', py: 2, px: 0.5 }}>
      {/* Brand mark + profile avatar */}
      <Tooltip title={displayName} placement="left" arrow>
        <Avatar
          onClick={handleProfileMenuOpen}
          sx={(t) => ({
            width: 46, height: 46,
            border: `2px solid ${t.palette.sacred.primary}`,
            bgcolor: 'sacred.primary', color: 'sacred.onPrimary',
            fontFamily: 'var(--sacred-font-display)', fontSize: '1.2rem',
            cursor: 'pointer',
          })}
        >
          {userInitial}
        </Avatar>
      </Tooltip>
      <Box sx={(t) => ({ width: 36, height: '1px', flexShrink: 0, bgcolor: t.palette.sacred.outlineVariant, opacity: 0.4, my: 1.5 })} />

      {/* Icon rail. A single pill is anchored under the active icon and
          morphs continuously between items: it slides vertically and, on
          hover, grows leftward out of the bar to reveal the label right
          next to the existing icon (Apple-style, rounded fillets). */}
      <Box
        onMouseLeave={() => setNavHoverIdx(null)}
        sx={{
          // Non-scrolling layer: holds the morphing pill (which grows leftward
          // out of the rail and therefore must NOT be clipped) and wraps the
          // scrollable icon list. flexGrow lets it claim the space between the
          // avatar (top) and the footer CTA (bottom); minHeight:0 lets the
          // inner list actually shrink-and-scroll instead of overflowing.
          position: 'relative',
          width: '100%',
          flexGrow: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflow: 'visible',
        }}
      >
        {highlightIdx >= 0 && (
          <Box
            aria-hidden
            sx={(t) => ({
              position: 'absolute',
              top: `${highlightIdx * (itemH + NAV_GAP) + 4 - railScroll}px`,
              right: 12,
              height: `${itemH - 8}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              // Fit the label exactly; max-width animates the reveal so the
              // pill grows only to the width it needs.
              width: 'max-content',
              maxWidth: hovering ? 360 : itemH - 8,
              pr: '52px',
              pl: hovering ? '20px' : 0,
              borderRadius: '16px',
              bgcolor: hovering ? 'sacred.primaryFixed' : `${t.palette.sacred.primary}29`,
              boxShadow: hovering
                ? `0 0 0 1px ${t.palette.sacred.primary}66, 0 12px 32px ${t.palette.sacred.primary}73, 0 0 26px ${t.palette.sacred.primary}66`
                : 'none',
              transition:
                'top 380ms cubic-bezier(0.22, 1, 0.36, 1), max-width 380ms cubic-bezier(0.22, 1, 0.36, 1), padding 320ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 320ms, background-color 280ms',
              pointerEvents: 'none',
              overflow: 'hidden',
              zIndex: 0,
            })}
          >
            <Typography
              sx={{
                fontFamily: 'var(--sacred-font-display)',
                fontSize: '1.15rem',
                fontWeight: 700,
                lineHeight: 1,
                textAlign: 'right',
                color: 'sacred.primaryContainer',
                whiteSpace: 'nowrap',
                opacity: hovering ? 1 : 0,
                transform: hovering ? 'translateX(0)' : 'translateX(10px)',
                transition: 'opacity 220ms ease, transform 360ms cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
              {sidebarItems[highlightIdx]?.text}
            </Typography>
          </Box>
        )}
        {/* Scrollable icon list. Scrolls only when the icons can't fit the
            actual window height (e.g. windowed mode on a short viewport) so
            the footer CTA below never gets pushed off-screen. Scrollbar is
            hidden; the highlight pill above offsets by railScroll to track. */}
        <Box
          ref={railRef}
          onScroll={(e) => setRailScroll(e.currentTarget.scrollTop)}
          sx={{
            width: '100%',
            flexGrow: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: `${NAV_GAP}px`,
            overflowY: 'auto',
            overflowX: 'visible',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
        {sidebarItems.map((item, i) => {
          const isHovered = i === navHoverIdx;
          const isActive = i === highlightIdx;
          return (
            <Box
              key={item.text}
              onMouseEnter={() => setNavHoverIdx(i)}
              onClick={() => handleMenuClick(item.path)}
              sx={(t) => ({
                position: 'relative',
                zIndex: 1,
                width: '100%',
                height: `${itemH}px`,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                // On a light pill the icon needs the dark-green container
                // tone for contrast; otherwise the brand green / muted tone.
                // BayanIcon paints from --bayan-icon-ink (it ignores CSS
                // `color`), so drive that variable to actually recolour it.
                '--bayan-icon-ink': isHovered
                  ? t.palette.sacred.primaryContainer
                  : (isActive ? t.palette.sacred.primary : t.palette.sacred.onSurfaceVariant),
                transition: 'color 220ms',
                '&:hover': { '--bayan-icon-ink': t.palette.sacred.primaryContainer },
              })}
            >
              {React.cloneElement(item.icon, { sx: { fontSize: 30 } })}
            </Box>
          );
        })}
        </Box>
      </Box>

      {/* Footer CTA as an icon button */}
      {!isParentRole(role) && !isStudentRole(role) && (
        <Tooltip title="تسجيل ورد اليوم" placement="left" arrow>
          <IconButton
            onClick={() => navigate('/daily-progress')}
            sx={{ mt: 1, bgcolor: 'sacred.primary', color: 'sacred.onPrimary', width: 46, height: 46, '&:hover': { bgcolor: 'sacred.primary', opacity: 0.9 } }}
          >
            {/* Contrast-aware brand icon: gold on the dark-green light-theme
                fill, dark-green on the light-mint dark-theme fill. A fixed gold
                sinks into the mint button (sacred.primary is #5FBF8E in dark). */}
            <BayanIcon name="edit_pencil" tone={toneForSurface(theme.palette.sacred.primary)} size={26} />
          </IconButton>
        </Tooltip>
      )}
      {isStudentRole(role) && (
        <Tooltip title={t('student.startReview')} placement="left" arrow>
          <IconButton
            onClick={() => navigate('/student/review')}
            sx={{ mt: 1, bgcolor: 'sacred.primary', color: 'sacred.onPrimary', width: 46, height: 46, '&:hover': { bgcolor: 'sacred.primary', opacity: 0.9 } }}
          >
            {/* Contrast-aware brand icon (see note on the staff CTA above). */}
            <BayanIcon name="lips_pronounce" tone={toneForSurface(theme.palette.sacred.primary)} size={26} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'sacred.surface' }} className="medersa-pattern">
      {/* Top AppBar — slides up out of view on scroll-down, back on scroll-up. */}
      <Slide appear={false} direction="down" in={!hideAppBar}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mr: { md: `${drawerWidth}px` },
          ml: 0,
          transition: (t) => t.transitions.create(['width', 'margin'], {
            easing: t.transitions.easing.sharp,
            duration: t.transitions.duration.enteringScreen,
          }),
          // iPhone: transparent AppBar so it blends with the page hero
          // (Revolut pattern — top bar is part of the gradient).
          bgcolor: {
            xs: (t) => `${t.palette.sacred.surface}E6`,
            md: undefined,
          },
          backdropFilter: { xs: 'blur(12px)', md: undefined },
          WebkitBackdropFilter: { xs: 'blur(12px)', md: undefined },
          borderBottom: {
            xs: (t) => `1px solid ${t.palette.sacred.outlineVariant}22`,
            md: undefined,
          },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, md: 72 }, height: { xs: 56, md: 72 }, gap: 2, px: { xs: 1.5, md: 3 } }}>
          {/* iPhone: Revolut-style top bar — greeting + avatar + bell only.
              Hides every desktop chrome element on xs. */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, width: '100%' }}>
            <MobileTopBar
              displayName={displayName}
              userInitial={userInitial}
              onProfileClick={handleProfileMenuOpen}
            />
          </Box>
          {/* Inline search — wired to student/class index */}
          <Box
            ref={(el) => setSearchAnchor(el)}
            sx={(th) => ({
              position: 'relative',
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              bgcolor: th.palette.sacred.surfaceContainerLow,
              borderRadius: '8px',
              pl: 1.5, pr: 1.5, py: 0.5,
              minWidth: 220, width: 'clamp(220px, 22vw, 320px)',
              borderBottom: `1px solid ${th.palette.sacred.outlineVariant}33`,
              transition: 'border-color 220ms',
              '&:focus-within': { borderBottomColor: th.palette.sacred.secondary },
            })}
          >
            <BayanIcon name="search" sx={{ color: 'sacred.outline', fontSize: 20, mr: 1 }} />
            <InputBase
              placeholder="بحث عن طالب أو حلقة..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={ensureSearchIndex}
              sx={{ fontFamily: 'var(--sacred-font-body)', fontSize: '0.9rem', width: '100%' }}
            />
          </Box>

          <Popper
            open={searchResults.length > 0 && Boolean(searchAnchor)}
            anchorEl={searchAnchor}
            placement="bottom-start"
            sx={{ zIndex: theme.zIndex.appBar + 10, minWidth: 260 }}
          >
            <ClickAwayListener onClickAway={() => setSearchQuery('')}>
              <Paper elevation={3} sx={(th) => ({
                mt: 1, p: 1, minWidth: 280,
                bgcolor: th.palette.sacred.surfaceContainerLowest,
                borderRadius: 2,
                boxShadow: `0 20px 60px -20px ${th.palette.sacred.ink}22`,
              })}>
                {searchResults.map((r, i) => (
                  <MenuItem
                    key={`${r.kind}-${r.id}-${i}`}
                    onClick={() => {
                      navigate(r.kind === 'student' ? `/students` : `/classes`);
                      setSearchQuery('');
                    }}
                    sx={{ borderRadius: 1, py: 1 }}
                  >
                    <ListItemIcon sx={{ color: 'sacred.primary' }}>{r.icon}</ListItemIcon>
                    <ListItemText
                      primary={<Typography sx={{ fontFamily: 'var(--sacred-font-body)', fontWeight: 600 }}>{r.label}</Typography>}
                      secondary={<Typography sx={{ fontFamily: 'var(--sacred-font-body)', fontSize: '0.75rem', color: 'sacred.onSurfaceVariant' }}>{r.sub}</Typography>}
                    />
                    <Chip size="small" label={r.kind === 'student' ? 'طالب' : 'حلقة'} sx={{ ml: 1, fontSize: '0.65rem' }} />
                  </MenuItem>
                ))}
              </Paper>
            </ClickAwayListener>
          </Popper>

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title={online ? t('offline.onlineTooltip') : t('offline.offlineTooltip')}>
            <Chip
              size="small"
              icon={online ? <CloudDone fontSize="small" /> : <CloudOff fontSize="small" />}
              label={online ? t('offline.online') : t('offline.offline')}
              variant="outlined"
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                borderColor: online ? 'sacred.primary' : 'sacred.warning',
                color: online ? 'sacred.primary' : 'sacred.warning',
                fontFamily: 'var(--sacred-font-label)',
                fontSize: '0.7rem',
              }}
            />
          </Tooltip>

          <SttHealthIndicator sx={{ ml: 0.5, display: { xs: 'none', sm: 'inline-flex' } }} />

          <Tooltip title={`الخط: ${font.option.labelAr}`}>
            <IconButton onClick={(e) => setFontAnchor(e.currentTarget)} sx={{ color: 'sacred.onSurface', display: { xs: 'none', sm: 'inline-flex' } }}>
              <TextFields />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={fontAnchor}
            open={Boolean(fontAnchor)}
            onClose={() => setFontAnchor(null)}
            PaperProps={{ sx: { minWidth: 260, py: 0.5 } }}
          >
            {font.options.map((opt) => (
              <MenuItem
                key={opt.key}
                selected={font.fontKey === opt.key}
                onClick={() => { font.setFont(opt.key); setFontAnchor(null); }}
                sx={(th) => ({
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25, py: 1.25,
                  borderInlineEnd: font.fontKey === opt.key ? `3px solid ${th.palette.sacred.secondary}` : '3px solid transparent',
                })}
              >
                <Typography
                  sx={{
                    fontFamily: opt.displayStack,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: 'sacred.primary',
                    lineHeight: 1.2,
                  }}
                >
                  {opt.labelAr}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'var(--sacred-font-label)',
                    fontSize: '0.7rem',
                    color: 'sacred.onSurfaceVariant',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  {opt.labelEn}
                </Typography>
              </MenuItem>
            ))}

            <Divider sx={{ my: 0.5 }} />
            <ListSubheader
              disableSticky
              sx={{
                fontFamily: 'var(--sacred-font-body)', fontWeight: 700,
                color: 'sacred.onSurface', lineHeight: 2, bgcolor: 'transparent',
              }}
            >
              خط التقارير (PDF)
            </ListSubheader>
            {REPORT_FONT_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.key}
                selected={reportFontKey === opt.key}
                onClick={() => {
                  setStoredReportFontKey(opt.key);
                  setReportFontKey(opt.key);
                  setFontAnchor(null);
                }}
                sx={(th) => ({
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.1, py: 0.9,
                  borderInlineEnd: reportFontKey === opt.key ? `3px solid ${th.palette.sacred.secondary}` : '3px solid transparent',
                })}
              >
                <Typography sx={{ fontFamily: 'var(--sacred-font-body)', fontSize: '0.95rem', fontWeight: 600, color: 'sacred.primary', lineHeight: 1.2 }}>
                  {opt.labelAr}
                </Typography>
                <Typography sx={{ fontFamily: 'var(--sacred-font-label)', fontSize: '0.65rem', color: 'sacred.onSurfaceVariant', letterSpacing: '0.04em' }}>
                  {opt.note || opt.family}
                </Typography>
              </MenuItem>
            ))}
          </Menu>


          <IconButton onClick={colorMode.toggleColorMode} sx={{ color: 'sacred.onSurface', display: { xs: 'none', sm: 'inline-flex' } }}>
            {theme.palette.mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
          </IconButton>

          <Box sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
            <NotificationCenter />
          </Box>

          <Box
            onClick={handleProfileMenuOpen}
            sx={(t) => ({
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center', gap: 1,
              bgcolor: t.palette.sacred.surfaceContainerHigh,
              // Avatar sits at the RTL start (right); give the text side (left)
              // the breathing room so the name never hugs the rounded end.
              borderRadius: 999, pr: 0.5, pl: 1.75, py: 0.5,
              cursor: 'pointer',
              maxWidth: 'clamp(160px, 20vw, 280px)',
              '&:hover': { bgcolor: t.palette.sacred.surfaceContainerHighest },
            })}
          >
            <Avatar sx={{ width: 34, height: 34, bgcolor: 'sacred.primary', color: 'sacred.onPrimary', fontFamily: 'var(--sacred-font-body)', fontSize: '0.95rem' }}>
              {userInitial}
            </Avatar>
            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '1px' }}>
              <Typography sx={{ fontFamily: 'var(--sacred-font-body)', color: 'sacred.onSurface', fontSize: '0.9rem', fontWeight: 600, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayName}
              </Typography>
              <Typography sx={{ fontFamily: 'var(--sacred-font-label)', color: 'sacred.onSurfaceVariant', fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.2 }}>
                {t(`roles.${role || 'teacher'}`)}
              </Typography>
            </Box>
          </Box>

          {/* xs profile avatar handled by MobileTopBar */}
        </Toolbar>
      </AppBar>
      </Slide>

      {/* Sidebar — permanent on md+. The temporary mobile Drawer was removed
          because the mobile nav uses MobileBottomNav (5-slot bottom bar with
          raised FAB) instead of a hamburger-driven side drawer. */}
      <Box
        component="nav"
        sx={{
          width: { md: drawerWidth },
          flexShrink: { md: 0 },
          transition: (t) => t.transitions.create('width', {
            easing: t.transitions.easing.sharp,
            duration: t.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Drawer
          variant="permanent"
          anchor="right"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              borderLeft: 'none',
              borderRight: 'none',
              // Let the hover pill grow out of the rail into the content area.
              overflow: 'visible',
              transition: (t) => t.transitions.create('width', {
                easing: t.transitions.easing.sharp,
                duration: t.transitions.duration.enteringScreen,
              }),
            },
          }}
          open
        >
          {sidebar}
        </Drawer>
      </Box>


      {/* Main */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: { xs: 2, md: 4, lg: 6 },
          py: { xs: 2, md: 3 },
          pb: { xs: `${MOBILE_BOTTOM_NAV_HEIGHT + 24}px`, md: 4 },
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'hidden',
          position: 'relative',
          transition: (t) => t.transitions.create('width', {
            easing: t.transitions.easing.sharp,
            duration: t.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Box sx={{ height: { xs: 56, md: 72 } }} />
        {authReady && !profileLoading && currentUser && !tenantId && (
          <Alert
            severity={autoRepair.error ? 'error' : 'warning'}
            variant="outlined"
            sx={{ mb: 2, borderRadius: 2 }}
            action={
              currentUser.email === 'jamal.mazraoui@director.albayan.com' ? (
                <Button
                  color="inherit"
                  size="small"
                  disabled={autoRepair.running}
                  onClick={async () => {
                    setAutoRepair({ running: true, attempted: true, error: '' });
                    try {
                      await repairDemoAdminProfile(currentUser);
                      if (typeof refreshProfile === 'function') await refreshProfile();
                      setAutoRepair({ running: false, attempted: true, error: '' });
                    } catch (err) {
                      console.error('[demo-repair]', err);
                      setAutoRepair({
                        running: false,
                        attempted: true,
                        error: err?.code || err?.message || 'خطأ غير معروف',
                      });
                    }
                  }}
                >
                  {autoRepair.running ? '...جاري الإصلاح' : 'إصلاح حساب المدير'}
                </Button>
              ) : null
            }
          >
            {autoRepair.error
              ? `تعذّر إصلاح الحساب: ${autoRepair.error}. تحقق من نشر قواعد Firestore الجديدة.`
              : t('errors.permissionDenied')}
          </Alert>
        )}
        <Box className="fade-in">
          <Outlet />
        </Box>
      </Box>

      {/* Mobile Bottom Navigation — Revolut-style with raised center FAB. */}
      <MobileBottomNav
        items={bottomItems}
        currentPath={location.pathname}
        onNavigate={handleMenuClick}
      />

      {/* Profile menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        slotProps={{
          paper: {
            sx: (t) => ({
              mt: 1,
              minWidth: 240,
              borderRadius: 3,
              boxShadow: `0 16px 40px -16px ${t.palette.mode === 'dark' ? '#000a' : '#0002'}, 0 0 0 1px ${t.palette.sacred.outlineVariant}33`,
              '& .MuiList-root': { py: 0.5 },
              '& .MuiMenuItem-root': {
                py: 1.25,
                px: 2,
                fontFamily: 'var(--sacred-font-body)',
                fontSize: '0.9rem',
                borderRadius: 1.5,
                mx: 0.5,
                my: 0.25,
                transition: 'background-color 180ms',
                '&:hover': {
                  bgcolor: `${t.palette.sacred.primary}10`,
                },
              },
              '& .MuiListItemIcon-root': {
                minWidth: 36,
                color: t.palette.sacred.onSurfaceVariant,
              },
            }),
          },
        }}
      >
        <Box sx={(t) => ({
          px: 2, py: 1.5,
          display: 'flex', alignItems: 'center', gap: 1.25,
        })}>
          <Avatar sx={(t) => ({
            width: 38, height: 38,
            bgcolor: t.palette.sacred.primary,
            color: t.palette.sacred.onPrimary,
            fontFamily: 'var(--sacred-font-body)',
            fontSize: '0.95rem',
            fontWeight: 600,
          })}>
            {userInitial}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography noWrap sx={{ fontFamily: 'var(--sacred-font-body)', fontWeight: 600, fontSize: '0.92rem', lineHeight: 1.25 }}>
              {currentUser?.displayName || currentUser?.email || currentUser?.phoneNumber || t('auth.defaultUserLabel')}
            </Typography>
            <Typography sx={{ color: 'sacred.onSurfaceVariant', fontSize: '0.72rem', fontFamily: 'var(--sacred-font-label)', letterSpacing: '0.04em', mt: 0.25 }}>
              {t(`roles.${role || 'teacher'}`)}
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ my: 0.25 }} />
        {!isParentRole(role) && !isStudentRole(role) && (
          <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/profile'); }}>
            <ListItemIcon><BayanIcon name="profile_avatar" fontSize="small" /></ListItemIcon>
            <ListItemText>{t('profile.title')}</ListItemText>
          </MenuItem>
        )}
        {role === ROLES.ADMIN && (
          <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/admin'); }}>
            <ListItemIcon><BayanIcon name="director_moudir" fontSize="small" /></ListItemIcon>
            <ListItemText>{t('navigation.admin')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={handleLogout}
          sx={(t) => ({
            color: t.palette.sacred.error,
            '& .MuiListItemIcon-root': { color: t.palette.sacred.error },
            '&:hover': { bgcolor: `${t.palette.sacred.errorContainer}55 !important` },
          })}
        >
          <ListItemIcon><BayanIcon name="logout_door" fontSize="small" /></ListItemIcon>
          <ListItemText>{t('auth.logout')}</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Layout;
